import Stack from '@mui/material/Stack';
import { colors, Typography } from '@sweep-io/sweep-design';
import CommentInputWithAvatar from '../../../../common/comments/CommentInputWithAvatar';
import CommentWithActions from '../../../../common/comments/CommentWithActions';
import { useConfigurationComments } from './useConfigurationComments';
import { useRunOnce } from '../../../../common/useRunOnce';
import { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectConfigurationComments } from '../../../../../reducers/documentation/documentationCardReducer';
import { CenteredCircularProgress } from '../../../../common/CenteredCircularProgress';
import type { MentionedUser } from '../../../../common/comments/types';
import DeleteCommentDialog from '../../../../common/comments/DeleteCommentDialog';
import { ConfigurationComment } from './types';
import { CommentHeader } from '../../header/CommentHeader';
import { SalesforceItem } from '../../../../parser/ParserTypes';

interface CommentsListProps {
  configurationItem: SalesforceItem;
  parentType: string;
  crmOrgId: string;
}

const SPACING = 2;

const CommentsList = ({ configurationItem, parentType, crmOrgId }: CommentsListProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [openCommentIdMenu, setOpenCommentIdMenu] = useState<string>();
  const [deleteCommentId, setDeleteCommentId] = useState<string>();

  const ref = useRef<HTMLDivElement | null>(null);

  const scrollToBottom = useCallback(() => {
    ref.current?.scrollTo({ top: ref.current.scrollHeight, behavior: 'smooth' });
  }, []);

  const {
    fetchConfigurationComments,
    updateConfigurationComment,
    deleteConfigurationComment,
    addConfigurationComment,
    pinConfigurationComment,
    isLoading: _isAddCommentLoading,
  } = useConfigurationComments();

  const comments = useSelector(selectConfigurationComments(crmOrgId, configurationItem.id));
  const pinnedComments = comments?.filter((comment) => comment.properties.pinned);

  useRunOnce(async () => {
    await fetchConfigurationComments(configurationItem.id, crmOrgId);
    setIsLoading(false);
  });

  const renderCommentWithAction = (comment: ConfigurationComment, isHighlighted: boolean) => (
    <CommentWithActions
      key={comment.id}
      reply={comment}
      isMenuOpen={openCommentIdMenu === comment.id}
      openMenu={() => setOpenCommentIdMenu(comment.id)}
      closeMenu={() => setOpenCommentIdMenu(undefined)}
      deleteReplyCb={() => setDeleteCommentId(comment.id)}
      editReplyCb={(commentBody, mentionedUserIds) => {
        const _comment = { ...comment, commentBody, mentionedUserIds };
        updateConfigurationComment(_comment, configurationItem.id, crmOrgId);
      }}
      deleteText={'Delete'}
      isPinned={comment.properties.pinned}
      isHighlighted={isHighlighted}
      pinReplyCb={() => {
        const _comment = {
          ...comment,
          properties: { pinned: !comment.properties.pinned },
        };
        pinConfigurationComment(_comment, configurationItem.id, crmOrgId);
      }}
    />
  );

  return (
    <>
      <CommentHeader
        configurationItem={configurationItem}
        parentType={parentType}
        crmOrgId={crmOrgId}
      />

      <Stack
        p={2}
        display="flex"
        flexDirection="column"
        width="100%"
        height="100%"
        overflow="hidden"
      >
        <Typography variant="h5-medium">Comments</Typography>

        {Boolean(deleteCommentId) && (
          <DeleteCommentDialog
            handleClose={() => setDeleteCommentId(undefined)}
            onDelete={() =>
              deleteCommentId &&
              deleteConfigurationComment(deleteCommentId, configurationItem.id, crmOrgId)
            }
          />
        )}

        {isLoading && <CenteredCircularProgress />}

        {!isLoading && (
          <Stack ml={-SPACING} mr={-SPACING} height="100%">
            {!!comments?.length && (
              <Stack
                pt={2}
                overflow="auto"
                height="100%"
                sx={{
                  '& .comment-with-actions': {
                    //hover state should touch edges of the dialog
                    pr: SPACING,
                    pl: SPACING,
                  },
                }}
                ref={ref}
              >
                {pinnedComments.map((comment) => renderCommentWithAction(comment, true))}
                {comments.map((comment) => renderCommentWithAction(comment, false))}
                {_isAddCommentLoading && <CenteredCircularProgress size={40} minHeight={'60px'} />}
              </Stack>
            )}

            <Stack
              width="100%"
              p={2}
              pb={0}
              sx={{
                background: colors.white,
                flex: '0 0 auto',
              }}
            >
              <CommentInputWithAvatar
                onConfirm={(commentBody: string, mentionedUserIds: MentionedUser[]) => {
                  addConfigurationComment(
                    { commentBody, mentionedUserIds, properties: { pinned: false } },
                    configurationItem.id,
                    crmOrgId,
                  );
                  scrollToBottom();
                }}
                placeholder="Add new comment. Use @ to mention"
              />
            </Stack>
          </Stack>
        )}
      </Stack>
    </>
  );
};

export default CommentsList;
